﻿import { combineReducers, configureStore } from "@reduxjs/toolkit";
import authReducer from "@/features/auth/authSlice";
import operationsReducer from "@/features/operations/operationsSlice";
import settingsReducer from "@/features/settings/settingsSlice";
import resetPasswordReducer from "@/features/auth/resetPasswordSlice";
import mapSchemaReducer from "@/features/mapSchema/mapSchemaSlice";
import rememberMeReducer from "@/features/auth/rememberMeSlice";
import crossReferenceReducer from "@/features/connections/crossReferenceSlice";
import artifactPreviewReducer from "@/features/connections/artifactPreviewSlice";
import manageCatalogDataSlice from "@/features/connections/manageCatalogDataSlice";
import connectionsBarSlice from "@/features/connectionsBar/connectionsBarSlice.js";
import breadcrumbsSlice from "@/features/breadcrumbs/breadcrumbsSlice";
import artifactsMapSlice from "@/features/homepage/artifactsMapSlice";
import { persistReducer, persistStore, createTransform } from "redux-persist";
import { thunk } from "redux-thunk";
import workflowManagerSlice from "@/features/homepage/workflowManagerSlice";
import homepageRecordsSlice from "@/features/homepage/recordsSlice";
import homeSlice from "@/features/homepage/homeSlice";
import integrationSlice from "@/features/connections/integrationSlice";
import alertSlice from "@/features/homepage/alertSliceList";
import maintenanceReducer from "@/features/maintenance/maintenanceSlice";
import fileImportSlice from "@/features/maintenance/fileImportSlice";
import retryRequestSlice from "@/features/auth/retryRequestSlice";
import shepDialogReducer from "@/features/shepDialog/shepDialogSlice";
import dayjs from "dayjs";
import dateFilterReducer from "@/features/dateFilterSlice.js";
import workflowManagerSessionSlice from "@/features/homepage/workflowManagerSessionSlice";
import userReducer from "@/features/auth/userSlice";

const storage = {
    getItem: (key) =>
        Promise.resolve(window.localStorage.getItem(key)),

    setItem: (key, value) =>
        Promise.resolve(window.localStorage.setItem(key, value)),

    removeItem: (key) =>
        Promise.resolve(window.localStorage.removeItem(key)),
};

const storageSession = {
    getItem: (key) =>
        Promise.resolve(window.sessionStorage.getItem(key)),

    setItem: (key, value) =>
        Promise.resolve(window.sessionStorage.setItem(key, value)),

    removeItem: (key) =>
        Promise.resolve(window.sessionStorage.removeItem(key)),
};
const persistLocalConfig = {
  key: "localInfo",
  storage: storage,
};

const dateFilterTransform = createTransform(
  // transform state on its way to being serialized and persisted, dayjs to ISO string
  (inboundState, key) => {
    if (key === "dateFilter") {
      const homeDF = inboundState?.home || { startDate: null, endDate: null };
      const integrationDF = inboundState?.integration || {
        startDate: null,
        endDate: null,
      };
      const messagesDF = inboundState?.messages || {
        startDate: null,
        endDate: null,
      };
      return {
        home: {
          startDate: homeDF.startDate
            ? dayjs(homeDF.startDate).toISOString()
            : null,
          endDate: homeDF.endDate ? dayjs(homeDF.endDate).toISOString() : null,
        },
        integration: {
          startDate: integrationDF.startDate
            ? dayjs(integrationDF.startDate).toISOString()
            : null,
          endDate: integrationDF.endDate
            ? dayjs(integrationDF.endDate).toISOString()
            : null,
        },
        messages: {
          startDate: messagesDF.startDate
            ? dayjs(messagesDF.startDate).toISOString()
            : null,
          endDate: messagesDF.endDate
            ? dayjs(messagesDF.endDate).toISOString()
            : null,
        },
      };
    }
    return inboundState;
  },
  // transform state being rehydrated, ISO string to dayjs object
  (outboundState, key) => {
    if (key === "dateFilter") {
      const homeDF = outboundState?.home || {};
      const integrationDF = outboundState?.integration || {};
      const messagesDF = outboundState?.messages || {};
      return {
        home: {
          startDate: homeDF.startDate ? dayjs(homeDF.startDate) : null,
          endDate: homeDF.endDate ? dayjs(homeDF.endDate) : null,
          ready: !!(homeDF.startDate && homeDF.endDate),
          source: homeDF.startDate && homeDF.endDate ? "session" : null,
        },
        integration: {
          startDate: integrationDF.startDate
            ? dayjs(integrationDF.startDate)
            : null,
          endDate: integrationDF.endDate ? dayjs(integrationDF.endDate) : null,
          ready: !!(integrationDF.startDate && integrationDF.endDate),
          source:
            integrationDF.startDate && integrationDF.endDate ? "session" : null, //to say "these came from a previous tab/session,” not from server defaults
        },
        messages: {
          startDate: messagesDF.startDate ? dayjs(messagesDF.startDate) : null,
          endDate: messagesDF.endDate ? dayjs(messagesDF.endDate) : null,
          ready: !!(messagesDF.startDate && messagesDF.endDate),
          source: messagesDF.startDate && messagesDF.endDate ? "session" : null,
        },
      };
    }
    return outboundState;
  },
  { whitelist: ["dateFilter"] },
);

const persistSessionConfig = {
  key: "sessionInfo",
  storage: storageSession,
  transforms: [dateFilterTransform],
  //only persist operations (full) and the new dateFilter slice
  //and the new workflowManagerSession slice
  whitelist: ["operations", "dateFilter", "workflowManagerSession"],
};

const rootReducer = (state, action) => {
  if (action.type === "auth/logOut/fulfilled") {
    // for all keys defined in your persistConfig(s)
    // remove data from session storage so the store has no data to rehydrate from
    // pass in the states we want to keep
    storageSession.removeItem("persist:sessionInfo");
    storage.removeItem("persist:localInfo");
    //sets store back to initial state
    const wipedState = combinedReducers(undefined, action); //everything set to initial
    //since putting in our own state (overwriting the persist reducers effect), need to re-add the persist property
    wipedState.localInfo._persist = state.localInfo._persist;
    wipedState.sessionInfo._persist = state.sessionInfo._persist;
    if (state.localInfo.rememberMe.userName !== null) {
      //save existing rememberMe info
      var rememberMeState = state.localInfo.rememberMe;
      //combine and stringify to match local storage format
      var localInitial = {
        rememberMe: JSON.stringify(rememberMeState),
      };
      // manually set data to persist in local storage
      storage.setItem("persist:localInfo", JSON.stringify(localInitial));
      wipedState.localInfo.rememberMe = rememberMeState;
      return combinedReducers(wipedState, action);
    }
    return combinedReducers(wipedState, action);
  }
  return combinedReducers(state, action);
};

const combinedLocalReducers = combineReducers({
  //slices we want to persist in local storage
  rememberMe: rememberMeReducer,
});
const combinedSessionReducers = combineReducers({
  //slices we want to persist in session storage
  user: userReducer,
  operations: operationsReducer,
  dateFilter: dateFilterReducer,
  workflowManagerSession: workflowManagerSessionSlice,
});
const combinedNonPersistedReducers = combineReducers({
  //slices we don't want to persist at all - will be refreshed on page reload
  auth: authReducer,
  resetPassword: resetPasswordReducer,
  settings: settingsReducer,
  mapSchema: mapSchemaReducer,
  crossReference: crossReferenceReducer,
  artifactPreview: artifactPreviewReducer,
  shepDialog: shepDialogReducer,
  manageCatalogData: manageCatalogDataSlice,
  connectionsBar: connectionsBarSlice,
  breadcrumbs: breadcrumbsSlice,
  artifactsMap: artifactsMapSlice,
  workflowManager: workflowManagerSlice,
  homepageRecords: homepageRecordsSlice,
  home: homeSlice,
  integration: integrationSlice,
  sendAlert: alertSlice.reducer,
  maintenance: maintenanceReducer,
  fileImport: fileImportSlice,
  retryRequest: retryRequestSlice,
});
const combinedReducers = combineReducers({
  //combine all top level reducers
  localInfo: persistReducer(persistLocalConfig, combinedLocalReducers),
  sessionInfo: persistReducer(persistSessionConfig, combinedSessionReducers),
  tempInfo: combinedNonPersistedReducers,
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) => {
    return [thunk]
    // or for TS users, use:
    // return new Tuple(myMiddleware)//[thunk],
  }
});

export const persistor = persistStore(store);
